// grouping-settings.ts — CTSK-501 (folder capture).
//
// Per-org settings under section `documents` that tune directory drops:
//   • groupingIgnore              names skipped when walking a dropped folder
//   • groupingMaxFilesPerDrop     cap on files taken from ONE drop (1-5000)
//   • groupingAutoNameFromFolder  off | prompt | always — name a group from
//                                 the root folder after the upload completes
//
// ONE normaliser, used by GET and PATCH /settings/documents (clamp on read AND
// on write, so a hand-edited row can never hand the client a nonsense cap)
// and by the web walker / confirmation dialog.
export const GROUPING_IGNORE_DEFAULT = [
    '.DS_Store',
    'Thumbs.db',
    'desktop.ini',
    '.git',
    'node_modules',
    '__MACOSX',
    '.cache',
    '.venv',
    '.vscode',
    '.idea',
    '__pycache__',
];
export const GROUPING_MAX_FILES_DEFAULT = 1000;
export const GROUPING_MAX_FILES_MIN = 1;
export const GROUPING_MAX_FILES_MAX = 5000;
/** The confirmation dialog warns above this many files. */
export const GROUPING_LARGE_DROP_WARNING = 500;
export const GROUPING_AUTO_NAME_MODES = ['off', 'prompt', 'always'];
export const GROUPING_AUTO_NAME_DEFAULT = 'prompt';
/** Bounds on a stored ignore list so a hand-edited row cannot balloon. */
export const GROUPING_IGNORE_MAX_ENTRIES = 200;
export const GROUPING_IGNORE_MAX_ENTRY_LENGTH = 255;
export function normalizeGroupingMaxFiles(value) {
    const n = typeof value === 'number' ? value : typeof value === 'string' && value.trim() !== '' ? Number(value) : NaN;
    if (!Number.isFinite(n))
        return GROUPING_MAX_FILES_DEFAULT;
    return Math.min(GROUPING_MAX_FILES_MAX, Math.max(GROUPING_MAX_FILES_MIN, Math.trunc(n)));
}
export function normalizeGroupingAutoName(value) {
    return GROUPING_AUTO_NAME_MODES.includes(value)
        ? value
        : GROUPING_AUTO_NAME_DEFAULT;
}
/**
 * Trim, drop empties / non-strings / names containing a path separator or
 * control character (an ignore entry is a single file or folder NAME, matched
 * exactly, case-sensitively), de-duplicate, cap. A non-array falls back to the
 * default list; an explicit empty array is respected ("ignore nothing").
 */
export function normalizeGroupingIgnore(value) {
    if (!Array.isArray(value))
        return [...GROUPING_IGNORE_DEFAULT];
    const out = [];
    const seen = new Set();
    for (const item of value) {
        if (typeof item !== 'string')
            continue;
        const name = item.trim().normalize('NFC');
        if (name === '' || name === '.' || name === '..')
            continue;
        if (/[\\/\u0000-\u001F\u007F]/.test(name))
            continue;
        if (Array.from(name).length > GROUPING_IGNORE_MAX_ENTRY_LENGTH)
            continue;
        if (seen.has(name))
            continue;
        seen.add(name);
        out.push(name);
        if (out.length >= GROUPING_IGNORE_MAX_ENTRIES)
            break;
    }
    return out;
}
/** The three folder-capture settings, defaulted and clamped, from a raw `documents` settings body. */
export function normalizeGroupingFolderSettings(body) {
    const b = (body && typeof body === 'object' ? body : {});
    return {
        groupingIgnore: normalizeGroupingIgnore(b.groupingIgnore),
        groupingMaxFilesPerDrop: normalizeGroupingMaxFiles(b.groupingMaxFilesPerDrop),
        groupingAutoNameFromFolder: normalizeGroupingAutoName(b.groupingAutoNameFromFolder),
    };
}
//# sourceMappingURL=grouping-settings.js.map